import { makeAPIRequest } from './surveyService';

// ─── Cache helper genérico (TTL + dedup de peticiones en vuelo) ──────────────
// Usado para datos de referencia (lectores QR, estados de alumnos) que se
// vuelven a pedir en cada visita a su tab/pantalla sin haber cambiado. Cada
// mutación asociada invalida el cache correspondiente para no servir stale data.
const CACHE_TTL = 2 * 60 * 1000; // 2 minutos

function createKeyedCache() {
  const entries   = new Map(); // key -> { data, time }
  const inFlight  = new Map(); // key -> Promise

  return {
    async get(key, fetcher) {
      const now = Date.now();
      const cached = entries.get(key);
      if (cached && (now - cached.time < CACHE_TTL)) {
        return cached.data;
      }

      if (inFlight.has(key)) {
        return inFlight.get(key);
      }

      const promise = (async () => {
        try {
          const data = await fetcher();
          entries.set(key, { data, time: Date.now() });
          return data;
        } finally {
          inFlight.delete(key);
        }
      })();

      inFlight.set(key, promise);
      return promise;
    },
    clear() {
      entries.clear();
      inFlight.clear();
    },
  };
}

// ─── Token ───────────────────────────────────────────────────────────────────

export const getOrCreateToken = (wpUserId, rut) =>
  makeAPIRequest(`/qr/token/${wpUserId}`, 'get', { rut });

export const revokeToken = (wpUserId, rut) =>
  makeAPIRequest(`/qr/token/${wpUserId}/revoke`, 'post', {}, { rut });

// ─── Validate ────────────────────────────────────────────────────────────────

export const validate = (data) =>
  makeAPIRequest('/qr/validate', 'post', {}, data);

// ─── Logs ────────────────────────────────────────────────────────────────────

export const getAccessLogs = (filters = {}) =>
  makeAPIRequest('/qr/logs', 'get', filters);

export const getStudentLogs = (rut) =>
  makeAPIRequest(`/qr/logs/student/${encodeURIComponent(rut)}`, 'get');

// data: { rut, wp_user_id?, scanner_id?, access_type?, location?, scanned_at?,
//   is_offline?, early_exit?, exit_reason?, exit_notes?, survey_response_id?,
//   guardian_present?, status_at_scan? }
// survey_response_id (opcional): id de la ficha de survey asociada al
// retiro (ver ModalAddSurveyResponse en ScannerShell) — solo aplica a
// exit_reason === 'pickup'.
export const logAccess = (data) =>
  makeAPIRequest('/qr/log', 'post', {}, data);

export const syncOffline = (records) =>
  makeAPIRequest('/qr/sync', 'post', {}, { records });

// ─── Cache & Scanners ────────────────────────────────────────────────────────

export const getStudentsCache = (page = 1, perPage = 100) =>
  makeAPIRequest('/qr/cache', 'get', { page, per_page: perPage });

export const generateAllTokens = () =>
  makeAPIRequest('/qr/tokens/generate-all', 'post');

const scannersCache = createKeyedCache();

export const listScanners = () =>
  scannersCache.get('all', () => makeAPIRequest('/qr/scanners', 'get'));

export const upsertScanner = async (data) => {
  const result = await makeAPIRequest('/qr/scanners', 'post', {}, data);
  scannersCache.clear();
  return result;
};

export const deleteScanner = async (scannerId) => {
  const result = await makeAPIRequest(`/qr/scanners/${scannerId}`, 'delete');
  scannersCache.clear();
  return result;
};

// ─── Student Statuses ──────────────────────────────────────────────────────

const studentStatusesCache = createKeyedCache();

export const listStudentStatuses = (filters = {}) =>
  studentStatusesCache.get(JSON.stringify(filters), () => makeAPIRequest('/qr/statuses', 'get', filters));

export const upsertStudentStatus = async (data) => {
  const result = await makeAPIRequest('/qr/statuses', 'post', {}, data);
  studentStatusesCache.clear();
  return result;
};

export const deleteStudentStatus = async (id) => {
  const result = await makeAPIRequest(`/qr/statuses/${id}`, 'delete');
  studentStatusesCache.clear();
  return result;
};

// ─── Pedagogical Exits ─────────────────────────────────────────────────────

export const listPedagogicalExits = (filters = {}) =>
  makeAPIRequest('/qr/pedagogical-exits', 'get', filters);

export const upsertPedagogicalExit = (data) =>
  makeAPIRequest('/qr/pedagogical-exits', 'post', {}, data);

export const deletePedagogicalExit = (id) =>
  makeAPIRequest(`/qr/pedagogical-exits/${id}`, 'delete');
